import {
  DEFAULT_TIMEZONE,
  formatActivityLocation,
  normalizeSiteLanguage,
  pickLocalized,
} from '@yoyoclub/shared';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';

import { DescriptionMarkdown } from '../components/DescriptionMarkdown';
import FollowHostButton from '../components/FollowHostButton';
import WaitlistJoinModal from '../components/booking/WaitlistJoinModal';
import ActivityReviews from '../components/reviews/ActivityReviews';
import DetailActions from '../components/site/DetailActions';
import Card from '../components/ui/Card';
import ImageGallery from '../components/ui/ImageGallery';
import PriceCard from '../components/ui/PriceCard';
import { useAuth } from '../hooks/useAuth';
import { dateLocale } from '../i18n/locale';
import { localize } from '../i18n/script';
import { passApi, type PassDetail } from '../services/passApi';
import {
  waitlistApi,
  type SessionWaitlistStatus as WaitlistStatusInfo,
} from '../services/waitlistApi';
import { getLocalizedTitle } from '../utils/activityTransform';
import { trackBookNowClick } from '../utils/analytics';

interface Activity {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  slug: string;
  descriptionCN: string | null;
  descriptionEN: string | null;
  category: string;
  location?: string; // legacy fallback; dropped after Phase 6 migration
  locationDetails: string;
  region?: string[];
  ageMin: number;
  ageMax: number;
  price: number;
  adultPrice: number;
  childRequired?: boolean;
  adultRequired?: boolean;
  maxParticipants: number;
  duration: number;
  refundPolicy: string | null;
  status: string;
  host: {
    displayName: string;
    avatarUrl?: string;
    bioZh?: string;
    bioEn?: string;
    id: string;
  };
  images: Array<{
    imageUrl: string;
    order: number;
    isCover: boolean;
  }>;
  sessions: Array<{
    id: string;
    startTime: string; // ISO string
    endTime: string; // ISO string
    effectiveBookingCutoffAt?: string; // ISO string; when registration closes (computed by the API)
    maxCapacity: number;
    currentBookings: number;
    price?: number | null; // Session child price in cents
    adultPrice?: number | null; // Session adult price in cents
    descriptionCN?: string | null;
    descriptionEN?: string | null;
  }>;
}

// Transform backend session to PriceCard format
interface TransformedSession {
  date: string;
  startTime: string;
  endTime: string;
  // Raw ISO end timestamp from the backend — used by CalendarSessionSelector to
  // determine whether a session has already ended (precise to the second).
  endTimestamp: string;
  startTimestamp?: string;
  cutoffTimestamp?: string; // when registration closes (effectiveBookingCutoffAt)
  maxCapacity: number;
  currentBookings?: number;
  price?: number | null; // cents, null = use activity default
  adultPrice?: number | null; // cents, null = use activity default
  description?: string | null;
}

export default function ActivityDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation(['activity', 'common', 'pass']);
  const { isAuthenticated } = useAuth();
  const [activity, setActivity] = useState<Activity | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [isBioTruncated, setIsBioTruncated] = useState(false);

  // Waitlist join modal state. Lazily fetches the session waitlist status
  // when the user clicks "Join Waitlist".
  const [waitlistModal, setWaitlistModal] = useState<{
    sessionId: string;
    activityTitle: string;
    sessionLabel: string;
    status: WaitlistStatusInfo;
  } | null>(null);

  // PRD §3.3 (b): if the user arrived from a waitlist email link with a
  // partial-spots release, show a banner explaining what happened.
  const waitlistHint = searchParams.get('waitlistHint');
  const waitlistIdParam = searchParams.get('waitlistId');
  const showPartialHint = waitlistHint === 'partial' && !!waitlistIdParam;
  const bioRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    if (bioRef.current) {
      setIsBioTruncated(bioRef.current.scrollHeight > bioRef.current.clientHeight);
    }
  }, [activity]);
  const [activityPasses, setActivityPasses] = useState<PassDetail[]>([]);

  useEffect(() => {
    const fetchActivity = async () => {
      try {
        setLoading(true);
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        const response = await fetch(`${apiUrl}/activities/slug/${slug}`);

        if (!response.ok) {
          throw new Error('Failed to fetch activity');
        }

        const result = await response.json();

        if (result.success && result.data) {
          setActivity(result.data);
        } else {
          setError('Activity not found');
        }
      } catch (err) {
        setError('Failed to load activity');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchActivity();
    }
  }, [slug]);

  // Fetch available passes for this activity
  useEffect(() => {
    if (!slug) return;
    const fetchPasses = async () => {
      try {
        const data = await passApi.getPassesForActivity(slug);
        setActivityPasses(data);
      } catch {
        // Non-critical
      }
    };
    fetchPasses();
  }, [slug]);

  const handleBook = (sessionIndex: number) => {
    // Navigate to booking checkout page
    const session = activity?.sessions[sessionIndex];
    if (session && activity) {
      trackBookNowClick(getLocalizedTitle(activity, i18n.language));
      const tc = searchParams.get('tc');
      const wl = searchParams.get('waitlistId');
      const params = new URLSearchParams({ sessionId: session.id });
      if (tc) params.set('tc', tc);
      if (wl) params.set('waitlistId', wl);
      navigate(`/booking/${slug}?${params.toString()}`);
    }
  };

  const handleJoinWaitlist = async (sessionIndex: number) => {
    const session = activity?.sessions[sessionIndex];
    if (!session || !activity) return;
    if (!isAuthenticated) {
      const target = `/activity/${slug}`;
      navigate(`/login?redirect=${encodeURIComponent(target)}`);
      return;
    }
    try {
      const status = await waitlistApi.getSessionStatus(session.id);
      const start = new Date(session.startTime);
      const sessionLabel = `${start.toLocaleDateString(dateLocale(i18n.language), {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: DEFAULT_TIMEZONE,
      })} · ${start.toLocaleTimeString(dateLocale(i18n.language), {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: DEFAULT_TIMEZONE,
      })}`;
      setWaitlistModal({
        sessionId: session.id,
        activityTitle: getLocalizedTitle(activity, i18n.language),
        sessionLabel,
        status,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load waitlist status', err);
    }
  };

  // TODO: Re-enable when wishlist feature is implemented
  // const handleAddToWishlist = () => {
  //   // TODO: Implement wishlist
  //   alert('Feature coming soon!');
  // };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading activity...</p>
        </div>
      </div>
    );
  }

  if (error || !activity) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {error || t('activity:detail.notFound')}
          </h1>
          <button onClick={() => navigate('/activities')} className="text-primary hover:underline">
            {t('activity:detail.backToActivities')}
          </button>
        </div>
      </div>
    );
  }

  // Category label resolved from i18n; falls back to enum value if no translation exists.
  const categoryLabel = (() => {
    const key = `common:category.${activity.category}`;
    const translated = t(key);
    return translated === key ? activity.category : translated;
  })();

  // Transform sessions from backend format to PriceCard format
  const transformedSessions: TransformedSession[] = activity.sessions.map((session) => {
    const startDate = new Date(session.startTime);
    const endDate = new Date(session.endTime);

    // Format date in activity timezone (America/Los_Angeles)
    // Using Intl.DateTimeFormat to get correct date components in the target timezone
    const dateFormatter = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      timeZone: DEFAULT_TIMEZONE,
    });
    const dateStr = dateFormatter.format(startDate); // Returns YYYY-MM-DD format

    return {
      date: dateStr,
      startTime: startDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: DEFAULT_TIMEZONE,
      }),
      endTime: endDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: DEFAULT_TIMEZONE,
      }),
      endTimestamp: session.endTime,
      startTimestamp: session.startTime,
      cutoffTimestamp: session.effectiveBookingCutoffAt,
      maxCapacity: session.maxCapacity,
      currentBookings: session.currentBookings,
      price: session.price,
      adultPrice: session.adultPrice,
      description: localize(session.descriptionCN, session.descriptionEN, i18n.language),
    };
  });

  // Determine if sessions have variable pricing
  const effectiveChildPrices = activity.sessions.map((s) => s.price ?? activity.price);
  const effectiveAdultPrices = activity.sessions.map((s) => s.adultPrice ?? activity.adultPrice);
  const hasVariablePricing =
    new Set(effectiveChildPrices).size > 1 || new Set(effectiveAdultPrices).size > 1;
  const minChildPrice = Math.min(...effectiveChildPrices);
  const minAdultPrice = Math.min(...effectiveAdultPrices);

  // If we arrived from a waitlist claim link, pre-select the matching session
  // so the user does not have to hunt for it in a multi-session activity.
  const sessionIdParam = searchParams.get('sessionId');
  const initialSelectedSession =
    sessionIdParam !== null ? activity.sessions.findIndex((s) => s.id === sessionIdParam) : -1;

  const priceCardElement = (
    <PriceCard
      price={(hasVariablePricing ? minChildPrice : activity.price) / 100}
      adultPrice={((hasVariablePricing ? minAdultPrice : activity.adultPrice) ?? 0) / 100}
      childRequired={activity.childRequired}
      sessions={transformedSessions}
      onBook={handleBook}
      onJoinWaitlist={handleJoinWaitlist}
      hasVariablePricing={hasVariablePricing}
      activityPriceCents={activity.price}
      activityAdultPriceCents={activity.adultPrice ?? 0}
      initialSelectedSession={initialSelectedSession >= 0 ? initialSelectedSession : undefined}
      passes={activityPasses}
    />
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Main Content */}
      <div className="container-custom py-4 sm:py-8">
        {showPartialHint && (
          <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            {t('waitlist:partialHint')}
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
          {/* Left Column - Details */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Image Gallery */}
            <ImageGallery
              images={activity.images}
              alt={getLocalizedTitle(activity, i18n.language)}
            />

            {/* Title */}
            <div>
              <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
                {getLocalizedTitle(activity, i18n.language)}
              </h1>
              <DetailActions
                className="mt-3"
                type="activity"
                targetId={activity.id}
                sharePath={`/activity/${activity.slug}`}
                title={getLocalizedTitle(activity, i18n.language)}
              />
            </div>

            {/* Quick Info Bar */}
            <div className="flex flex-wrap items-center gap-4 sm:gap-6 text-xs sm:text-sm text-gray-600">
              {/* Age Range */}
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
                <span>
                  {t('common:ages.label')}{' '}
                  {activity.ageMax >= 99
                    ? `${activity.ageMin}+`
                    : `${activity.ageMin}-${activity.ageMax}`}
                </span>
              </div>

              {/* Duration */}
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <span>
                  {(() => {
                    const h = Math.floor(activity.duration / 60);
                    const m = activity.duration % 60;
                    const hLabel = t('common:duration.hours');
                    const mLabel = t('common:duration.minutes');
                    if (h > 0 && m > 0) return `${h}${hLabel} ${m}${mLabel}`;
                    if (h > 0) return `${h}${hLabel}`;
                    return `${m}${mLabel}`;
                  })()}
                </span>
              </div>

              {/* Category */}
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                  />
                </svg>
                <span>{categoryLabel}</span>
              </div>

              {/* Location */}
              <div className="flex items-center gap-2">
                <svg
                  className="w-4 h-4 sm:w-5 sm:h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span>
                  {formatActivityLocation({
                    region: activity.region,
                    locationDetails: activity.locationDetails,
                    fallbackLocation: activity.location,
                    locale: normalizeSiteLanguage(i18n.language),
                  })}
                </span>
              </div>
            </div>

            {/* Mobile: Price Card (shown above description on mobile) */}
            <div className="lg:hidden">{priceCardElement}</div>

            {/* Description Section */}
            <Card padding="lg">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('activity:detail.about')}
              </h2>
              <div className="relative">
                <div
                  className={`prose prose-sm max-w-none text-gray-700 prose-headings:text-base prose-headings:sm:text-lg prose-headings:font-semibold prose-p:text-sm prose-p:sm:text-base prose-p:leading-relaxed overflow-hidden transition-all duration-300 ${
                    isDescriptionExpanded ? '' : 'max-h-48 sm:max-h-none'
                  }`}
                >
                  <DescriptionMarkdown lang={i18n.language}>
                    {pickLocalized(activity.descriptionCN, activity.descriptionEN, i18n.language)}
                  </DescriptionMarkdown>
                </div>
                {/* Gradient overlay and expand button - mobile only */}
                {!isDescriptionExpanded && (
                  <div className="sm:hidden absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white to-transparent pointer-events-none" />
                )}
                {!isDescriptionExpanded && (
                  <button
                    onClick={() => setIsDescriptionExpanded(true)}
                    className="sm:hidden w-full mt-2 py-2 text-primary font-medium text-sm flex items-center justify-center gap-1"
                  >
                    {t('activity:detail.showMore')}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                )}
                {isDescriptionExpanded && (
                  <button
                    onClick={() => setIsDescriptionExpanded(false)}
                    className="sm:hidden w-full mt-2 py-2 text-primary font-medium text-sm flex items-center justify-center gap-1"
                  >
                    {t('activity:detail.showLess')}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 15l7-7 7 7"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </Card>

            {/* Host Info Card */}
            <Card padding="lg">
              <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">
                {t('activity:detail.aboutHost')}
              </h2>
              <div className="flex items-start gap-4">
                {activity.host.avatarUrl ? (
                  <img
                    src={activity.host.avatarUrl}
                    alt={activity.host.displayName}
                    className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-primary">
                      {activity.host.displayName.charAt(0)}
                    </span>
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="text-base font-semibold text-gray-900">
                    {activity.host.displayName}
                  </h3>
                  {localize(activity.host.bioZh, activity.host.bioEn, i18n.language) && (
                    <div className="mt-1">
                      <p
                        ref={bioRef}
                        className={`text-sm text-gray-600 ${isBioExpanded ? '' : 'line-clamp-3 sm:line-clamp-none'}`}
                      >
                        {localize(activity.host.bioZh, activity.host.bioEn, i18n.language)}
                      </p>
                      {(isBioTruncated || isBioExpanded) && (
                        <button
                          onClick={() => setIsBioExpanded(!isBioExpanded)}
                          className="sm:hidden text-sm text-primary font-medium mt-1 flex items-center gap-1"
                        >
                          {isBioExpanded
                            ? t('activity:detail.showLess')
                            : t('activity:detail.showMore')}
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d={isBioExpanded ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
                            />
                          </svg>
                        </button>
                      )}
                    </div>
                  )}
                  <Link
                    to={`/activities?hostId=${activity.host.id}`}
                    className="inline-block text-sm text-primary hover:underline mt-2"
                  >
                    {t('activity:detail.viewMoreByHost')}
                  </Link>
                  <FollowHostButton hostId={activity.host.id} className="mt-3" />
                </div>
              </div>
            </Card>

            {/* Parent Reviews */}
            <ActivityReviews key={activity.id} activityId={activity.id} />
          </div>

          {/* Right Column - Price Card (desktop only) */}
          <div className="hidden lg:block lg:col-span-1">{priceCardElement}</div>
        </div>
      </div>
      {waitlistModal && (
        <WaitlistJoinModal
          sessionId={waitlistModal.sessionId}
          activityTitle={waitlistModal.activityTitle}
          sessionLabel={waitlistModal.sessionLabel}
          childRequired={activity.childRequired ?? true}
          adultRequired={activity.adultRequired ?? true}
          currentWaitlistCount={waitlistModal.status.waitlistCount}
          onClose={() => setWaitlistModal(null)}
          onJoined={() => {
            setWaitlistModal(null);
            navigate('/dashboard');
          }}
        />
      )}
    </div>
  );
}
